/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  AutocompleteInput component
 */

import { Input } from '@chakra-ui/react'
import { Field } from 'formik'
import React, { useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import type * as Api from '../../../../shared/api'
import {
  AUTOCOMPLETE_INPUT_ID,
  type AutocompleteOrigin,
  type AutocompleteTheme,
} from '../../../../shared/components/Autocomplete/constants'
import { handleKeypress } from '../../../../shared/components/Autocomplete/module/thunks'
import { clearFetchingKey } from '../../../../shared/modules/autocomplete/actions'
import { useAutocompleteContext } from '../AutocompleteContext'
import { getIdFromOrigin } from '../helpers'

export interface AutocompleteInputProps {
  ariaDescribedby: string | undefined
  exclude: Parameters<(typeof Api)['autocompleteResults']['query']>[0]['exclude']
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  origin: AutocompleteOrigin | undefined
  placeholder: string | undefined
  shouldFocusAutoComplete?: boolean | undefined
  showMobileAutocomplete: boolean | undefined
  theme: AutocompleteTheme
}

export const AutocompleteInput = ({
  ariaDescribedby,
  exclude,
  onChange,
  origin,
  placeholder,
  shouldFocusAutoComplete,
  showMobileAutocomplete,
  theme,
}: AutocompleteInputProps) => {
  const dispatch = useDispatch()
  const {
    hoveredIndex,
    isFocused,
    keystrokes,
    predictionResults,
    searchTerm,
    setHoveredIndex,
    setIsFocused,
    setKeystrokes,
  } = useAutocompleteContext()

  const onBlur = () => {
    setIsFocused(false)
    dispatch(clearFetchingKey())
    setTimeout(() => {
      setHoveredIndex(-1)
    })
  }

  const onFocus = () => {
    setIsFocused(true)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    handleKeypress(
      e,
      origin,
      exclude,
      searchTerm,
      predictionResults,
      keystrokes,
      setHoveredIndex,
      setIsFocused,
      setKeystrokes
    )
  }

  const myRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    showMobileAutocomplete && onFocus?.()
  }, [onFocus, showMobileAutocomplete])

  useEffect(() => {
    if (isFocused) {
      myRef?.current?.focus()
    } else {
      myRef?.current?.blur()
    }
  }, [isFocused, showMobileAutocomplete])

  useEffect(() => {
    if (shouldFocusAutoComplete) {
      onFocus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFocusAutoComplete])

  const originId = getIdFromOrigin(origin)
  return (
    <Input
      autoFocus
      aria-activedescendant={
        hoveredIndex !== null && hoveredIndex !== -1
          ? `${originId}-results-${hoveredIndex}`
          : undefined
      }
      aria-autocomplete="list"
      aria-controls={`${originId}-results`}
      aria-describedby={ariaDescribedby}
      aria-expanded={isFocused}
      aria-label={placeholder}
      as={Field}
      autoComplete="off"
      className={theme.input}
      id={`${AUTOCOMPLETE_INPUT_ID}__${originId}`}
      innerRef={myRef}
      name="searchTerm"
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      role="combobox"
      type="search"
    />
  )
}
